import { computed, readonly, ref } from 'vue'
import { isPortalPreviewActive } from './portalPreview'
import { onlineManager, type QueryClient, type QueryKey } from '@tanstack/vue-query'
import type { AuthSession } from '@/api/types'

export const PORTAL_OFFLINE_TTL = 24 * 60 * 60 * 1000
export const PORTAL_OFFLINE_MAX_CHARS = 300_000
const PREFIX = 'avoschool.portal-offline.v1.'
const POINTER = `${PREFIX}account`
const networkOffline = ref(typeof navigator !== 'undefined' && !navigator.onLine)
const verificationRequired = ref(false)
const snapshotAt = ref<number | null>(null)
const enabled = ref(false)
let activeSession: AuthSession | null = null
let authenticatedAt = 0
let client: QueryClient | null = null
let restoring = false

export const portalOfflineState = {
  enabled: readonly(enabled),
  readonly: computed(() => enabled.value && (networkOffline.value || verificationRequired.value)),
  networkOffline: readonly(networkOffline),
  verificationRequired: readonly(verificationRequired),
  snapshotAt: readonly(snapshotAt),
}

export interface PortalOfflineQuery { key: QueryKey; data: unknown; updatedAt: number }
export interface PortalOfflineSnapshot {
  version: 1
  session: AuthSession
  authenticatedAt: number
  queries: PortalOfflineQuery[]
}
const object = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {}
const text = (value: unknown, max = 4000): string => typeof value === 'string' ? value.length > max ? `${value.slice(0, max - 1)}…` : value : ''
const array = (value: unknown): unknown[] => Array.isArray(value) ? value : []
const strings = (value: unknown): string[] => array(value).slice(0, 30).map(value => text(value, 160))
const keyFor = (accountId: string): string => `${PREFIX}${encodeURIComponent(accountId)}`
function people(value: unknown) {
  return array(value).slice(0, 30).map(value => {
    const item = object(value)
    return { id: text(item.id, 160), name: text(item.name, 160), timezone: text(item.timezone, 80), gradeLevel: text(item.gradeLevel, 80) }
  })
}
function minimalSession(value: unknown): AuthSession | null {
  const item = object(value)
  if (!text(item.accountId, 160) || !Array.isArray(item.permissions)) return null
  return {
    accountId: text(item.accountId, 160), login: '', displayName: text(item.displayName, 160),
    roles: strings(item.roles).filter(role => ['student', 'parent', 'teacher'].includes(role)),
    // These permissions only select the portal UI. Every write is blocked until the server revalidates the session.
    permissions: strings(item.permissions).filter(permission => permission.startsWith('portal.')),
  }
}
function lesson(value: unknown) {
  const item = object(value)
  return {
    id: text(item.id, 160), title: text(item.title, 200), description: text(item.description, 1000), subject: text(item.subject, 200),
    courseName: text(item.courseName, 200), status: text(item.status, 40), lessonType: text(item.lessonType, 40),
    startsAt: text(item.startsAt, 80), endsAt: text(item.endsAt, 80), classroomAccessClosesAt: text(item.classroomAccessClosesAt, 80),
    teacherIds: strings(item.teacherIds), studentIds: strings(item.studentIds), teacherNames: strings(item.teacherNames),
    studentNames: strings(item.studentNames), studentDetails: people(item.studentDetails), trial: item.trial === true, free: item.free === true,
  }
}
function message(value: unknown) {
  const item = object(value)
  const reply = object(item.replyTo)
  return {
    id: text(item.id, 160), conversationId: text(item.conversationId, 160), authorType: text(item.authorType, 40),
    authorName: item.authorType === 'admin' ? 'Поддержка Avoschool' : text(item.authorName, 160), body: item.deletedAt ? null : text(item.body, 2000) || (array(item.attachments).length ? 'Вложение доступно после подключения к сети.' : ''),
    version: typeof item.version === 'number' ? item.version : 0,
    editedAt: text(item.editedAt, 80) || null, deletedAt: text(item.deletedAt, 80) || null,
    replyTo: !item.deletedAt && item.replyTo ? { id: text(reply.id, 160), authorType: text(reply.authorType, 40) || null,
      authorName: reply.authorType === 'admin' ? 'Поддержка Avoschool' : reply.authorType ? text(reply.authorName, 160) : 'Сообщение',
      body: reply.deletedAt ? null : text(reply.body, 500), hasAttachments: !reply.deletedAt && reply.hasAttachments === true,
      version: typeof reply.version === 'number' ? reply.version : 0, deletedAt: text(reply.deletedAt, 80) || null } : null,
    attachments: [], mine: item.mine === true, createdAt: text(item.createdAt, 80),
    clientMessageId: text(item.clientMessageId, 160) || null,
    deliveryStatus: item.deliveryStatus === 'pending' || item.deliveryStatus === 'failed' ? item.deliveryStatus : 'sent',
    read: item.mine === true && item.deliveryStatus !== 'pending' && item.deliveryStatus !== 'failed' && item.read === true,
    failureCode: text(item.failureCode, 160) || null, failureMessage: text(item.failureMessage, 1000) || null,
  }
}

/** Explicit allowlist: never serialize complete API objects, finance, admin data, launch URLs or attachments. */
export function sanitizePortalOfflineQuery(key: QueryKey, data: unknown, accountId: string): unknown | undefined {
  if (key[0] !== 'portal') return undefined
  if (key[1] === 'me' && key.length === 2) {
    const item = object(data)
    if (item.accountId !== accountId) return undefined
    return { accountId, organizationTimezone: text(item.organizationTimezone, 80), displayName: text(item.displayName, 160), roles: strings(item.roles).filter(role => ['student', 'parent', 'teacher'].includes(role)), students: people(item.students), teachers: people(item.teachers) }
  }
  if (key[1] === 'lessons' || key[1] === 'home' && key[2] === 'lessons') return array(data).slice(0, 96).map(lesson)
  if (key[1] === 'lesson' && key.length === 3) return lesson(data)
  if (key[1] === 'chats' && key[2] === accountId) return array(data).slice(0, 20).map(value => {
    const item = object(value)
    return {
      id: text(item.id, 160), kind: text(item.kind, 40), title: text(item.title, 160), subtitle: text(item.subtitle, 200),
      participantType: text(item.participantType, 40), studentId: text(item.studentId, 160), staffId: text(item.staffId, 160),
      lastMessage: text(item.lastMessage, 500), lastMessageAt: text(item.lastMessageAt, 80), lastMessageAuthorType: text(item.lastMessageAuthorType, 40),
      lastMessageMine: item.lastMessageMine === true, unreadCount: Number.isFinite(item.unreadCount) ? item.unreadCount : 0, updatedAt: text(item.updatedAt, 80),
    }
  })
  // Search results and older pagination cursors are not an offline archive. Keep the latest text only.
  if (key[1] === 'chat-messages' && key[2] === accountId && (key[4] === '' || key[4] === undefined)) {
    const pages = array(object(data).pages)
    const messages = pages.slice().reverse().flatMap(page => array(object(page).messages)).slice(-60).map(message)
    return { pages: [{ messages, nextCursor: null }], pageParams: [null] }
  }
  return undefined
}

function storage(): Storage | null { try { return window.localStorage } catch { return null } }
function fresh(timestamp: number, now: number): boolean { return Number.isFinite(timestamp) && timestamp > 0 && timestamp <= now + 60_000 && now - timestamp < PORTAL_OFFLINE_TTL }
export function purgePortalOffline(accountId?: string | null): void {
  if (isPortalPreviewActive()) return
  const saved = storage()
  try {
    const account = accountId ?? saved?.getItem(POINTER)
    if (account) saved?.removeItem(keyFor(account))
    if (!accountId || saved?.getItem(POINTER) === accountId) saved?.removeItem(POINTER)
  } catch { /* Logout must work even when browser storage is disabled. */ }
  if (!accountId || activeSession?.accountId === accountId) {
    activeSession = null
    authenticatedAt = 0
    snapshotAt.value = null
  }
}

export function readPortalOfflineSnapshot(now = Date.now()): PortalOfflineSnapshot | null {
  if (isPortalPreviewActive()) return null
  try {
    const saved = storage()
    const account = saved?.getItem(POINTER)
    if (!account) return null
    const raw = saved?.getItem(keyFor(account))
    if (!raw || raw.length > PORTAL_OFFLINE_MAX_CHARS) { purgePortalOffline(account); return null }
    const value = JSON.parse(raw) as PortalOfflineSnapshot
    const session = minimalSession(value.session)
    if (value.version !== 1 || !session || session.accountId !== account || !session.permissions.includes('portal.read') || !fresh(value.authenticatedAt, now)) {
      purgePortalOffline(account); return null
    }
    const queries = array(value.queries).slice(0, 24).flatMap(raw => {
      const item = object(raw)
      if (!Array.isArray(item.key) || item.key.length > 8 || !fresh(Number(item.updatedAt), now)) return []
      const data = sanitizePortalOfflineQuery(item.key, item.data, account)
      return data === undefined ? [] : [{ key: item.key, data, updatedAt: Number(item.updatedAt) }]
    })
    if (!queries.some(query => query.key[1] === 'me')) { purgePortalOffline(account); return null }
    return { version: 1, session, authenticatedAt: value.authenticatedAt, queries }
  } catch { purgePortalOffline(); return null }
}

function saveSnapshot(): void {
  if (!enabled.value || !client || !activeSession || restoring || portalOfflineState.readonly.value) return
  const session = minimalSession(activeSession)
  if (!session?.permissions.includes('portal.read')) return
  const now = Date.now()
  const candidates = client.getQueryCache().getAll().filter(query => query.state.status === 'success' && fresh(query.state.dataUpdatedAt, now))
    .sort((a, b) => Number(b.queryKey[1] === 'me') - Number(a.queryKey[1] === 'me') || b.state.dataUpdatedAt - a.state.dataUpdatedAt)
  const queries: PortalOfflineQuery[] = []
  const snapshot: PortalOfflineSnapshot = { version: 1, session, authenticatedAt, queries }
  let messageQueries = 0
  for (const query of candidates) {
    if (queries.length >= 24 || query.queryKey[1] === 'chat-messages' && messageQueries >= 8) continue
    const data = sanitizePortalOfflineQuery(query.queryKey, query.state.data, session.accountId)
    if (data === undefined) continue
    const item = { key: query.queryKey, data, updatedAt: query.state.dataUpdatedAt }
    queries.push(item)
    if (JSON.stringify(snapshot).length > PORTAL_OFFLINE_MAX_CHARS) { queries.pop(); continue }
    if (query.queryKey[1] === 'chat-messages') messageQueries += 1
  }
  try {
    const saved = storage()
    saved?.setItem(keyFor(session.accountId), JSON.stringify(snapshot))
    saved?.setItem(POINTER, session.accountId)
    snapshotAt.value = queries.length ? Math.min(...queries.map(query => query.updatedAt)) : null
  } catch { /* Storage is optional; never replace a working page with a quota error. */ }
}

export function acceptPortalOnlineSession(session: AuthSession): void {
  if (isPortalPreviewActive() || session.portalPreview) return
  if (!enabled.value) return
  const previous = activeSession?.accountId ?? storage()?.getItem(POINTER)
  if (previous && previous !== session.accountId) purgePortalOffline(previous)
  const previousSnapshot = readPortalOfflineSnapshot()
  activeSession = session
  authenticatedAt = Date.now()
  // Keep the last visited schedule and chats across full online navigations too. The server has just verified this account.
  if (client && previousSnapshot?.session.accountId === session.accountId) {
    restoring = true
    try {
      for (const query of previousSnapshot.queries) {
        if ((client.getQueryState(query.key)?.dataUpdatedAt ?? 0) >= query.updatedAt) continue
        client.setQueryData(query.key, query.data, { updatedAt: query.updatedAt })
        void client.invalidateQueries({ queryKey: query.key, exact: true, refetchType: 'none' })
      }
    } finally { restoring = false }
  }
  networkOffline.value = !navigator.onLine
  verificationRequired.value = false
  onlineManager.setOnline(!networkOffline.value)
  saveSnapshot()
}

/** Called only after an actual network failure, never after an HTTP rejection. */
export function restorePortalOfflineSession(): AuthSession | null {
  if (!enabled.value || !client) return null
  const snapshot = readPortalOfflineSnapshot()
  if (!snapshot) return null
  activeSession = snapshot.session
  authenticatedAt = snapshot.authenticatedAt
  verificationRequired.value = true
  networkOffline.value = !navigator.onLine
  onlineManager.setOnline(false)
  restoring = true
  try {
    snapshot.queries.forEach(query => client!.setQueryData(query.key, query.data, { updatedAt: query.updatedAt }))
    snapshotAt.value = Math.min(...snapshot.queries.map(query => query.updatedAt))
  } finally { restoring = false }
  return snapshot.session
}

export function suspendPortalOfflineSession(): void {
  if (!enabled.value) return
  activeSession = null
  snapshotAt.value = null
  verificationRequired.value = true
  onlineManager.setOnline(false)
}

export function assertPortalMutationOnline(): void {
  if (portalOfflineState.readonly.value) throw new Error('Для этого действия нужно подключение к сети. После подключения повторите действие.')
}

export function installPortalOffline(queryClient: QueryClient, onReconnect: () => void): () => void {
  if (isPortalPreviewActive()) return () => undefined
  enabled.value = true
  client = queryClient
  networkOffline.value = !navigator.onLine
  const previousDefaults = queryClient.getDefaultOptions()
  // A click made offline must fail immediately, not become a payment/send/completion on reconnect.
  queryClient.setDefaultOptions({ ...previousDefaults, mutations: { ...previousDefaults.mutations, networkMode: 'always', retry: false } })
  const unsubscribe = queryClient.getQueryCache().subscribe(event => {
    if (event.type === 'updated' && event.action.type === 'success') {
      if (!event.action.manual && activeSession && !portalOfflineState.readonly.value
        && sanitizePortalOfflineQuery(event.query.queryKey, event.query.state.data, activeSession.accountId) !== undefined) authenticatedAt = Date.now()
      saveSnapshot()
    }
  })
  onlineManager.setEventListener(setOnline => {
    const offline = () => { networkOffline.value = true; verificationRequired.value = true; setOnline(false) }
    const online = () => { networkOffline.value = false; verificationRequired.value = true; setOnline(false); onReconnect() }
    window.addEventListener('offline', offline)
    window.addEventListener('online', online)
    setOnline(!networkOffline.value)
    return () => { window.removeEventListener('offline', offline); window.removeEventListener('online', online) }
  })
  const expiryTimer = window.setInterval(() => {
    if (portalOfflineState.readonly.value && activeSession && (!fresh(authenticatedAt, Date.now())
      || snapshotAt.value !== null && !fresh(snapshotAt.value, Date.now()))) {
      purgePortalOffline()
      queryClient.clear()
      window.dispatchEvent(new CustomEvent('avoschool:auth-required'))
    } else if (!activeSession) { readPortalOfflineSnapshot() }
  }, 60_000)
  return () => {
    window.clearInterval(expiryTimer)
    unsubscribe()
    enabled.value = false; client = null; activeSession = null; verificationRequired.value = false; snapshotAt.value = null
    queryClient.setDefaultOptions(previousDefaults)
    onlineManager.setEventListener(setOnline => {
      const update = () => setOnline(navigator.onLine)
      window.addEventListener('online', update); window.addEventListener('offline', update)
      return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update) }
    })
    onlineManager.setOnline(navigator.onLine)
  }
}
